import {
  ACHIEVEMENT_BY_ID,
  arcadeChapterOfLevel,
  ARCHETYPE_BY_ID,
  CLAW_TUTORIAL_BY_ID,
  chapterOfLevel,
  COLOR_LAB_TUTORIAL_BY_ID,
  colorChapterOfLevel,
  COSMETIC_BY_ID,
  fillChapterOfLevel,
  logicChapterOfLevel,
  SILK_TUTORIAL_BY_ID,
  TILE_TUTORIAL_BY_ID,
  tileChapterOfLevel,
  TOWER_TUTORIAL_BY_ID,
  TUTORIAL_BY_ID,
} from "@nano/content";
import { sanitizeDisplayName, type AvatarId, type Equipment, type EquipmentSlot, type LevelProof } from "@nano/contracts";
import { defaultGameConfig, isHousePlayable, type HouseId } from "@nano/game-config";
import { BUILD } from "../env.ts";
import { jadeVi } from "../i18n/tilecastle.vi.ts";
import { friendlyError, vi } from "../i18n/vi.ts";
import { loadHouseModule } from "../game/houses/registry.ts";
import { unannounced } from "../services/achievements.ts";
import { registerTestHooks } from "../game/testHooks.ts";
import { ApiClient, ApiClientError } from "../services/api/client.ts";
import { installAudioUnlock } from "../services/audio/engine.ts";
import { setMusicEnabled, setMusicTrack } from "../services/audio/music.ts";
import { play, playPlace, setSoundEnabled, unlockAudio } from "../services/audio/sfx.ts";
import { setVibrationEnabled, vibrate } from "../services/haptics.ts";
import { platform } from "../services/platform.ts";
import { preloadAll, preloadDoneForThisVersion } from "../services/update/preload.ts";
import { startAds } from "../services/ads/ads.ts";
import { retryAdClaims } from "./adActions.ts";
import { publicOrigin, startNativeUpdates } from "../services/update/updates.ts";
import {
  bestStars,
  bestTime,
  emptySave,
  highestCompleted,
  predictGold,
  recordLocalResult,
  recordPlayStats,
  withAvatar,
} from "../services/save/progress.ts";
import { openSaveStore } from "../services/save/store.ts";
import type { Settings } from "../services/save/types.ts";
import { SyncService } from "../services/sync/SyncService.ts";
import { bridge } from "./bridge.ts";
import { commitResult, getSave, initPersistence, refreshOutbox, updateSave } from "./persist.ts";
import { appStore, setUi, toast, type Overlay, type PuzzleHouseId, type PuzzleLevel } from "./store.ts";

let sync: SyncService | null = null;

export function requireSync(): SyncService {
  if (!sync) throw new Error("sync not started");
  return sync;
}

function applySettings(settings: Settings): void {
  setSoundEnabled(settings.sound);
  setMusicEnabled(settings.music);
  setVibrationEnabled(settings.vibration);
}

/** Each chapter's puzzle music sits in its own key, so chapters feel distinct. */
const CHAPTER_KEYS = [0, 2, -3, 3, -2, 5, -4, 1, 4, 0];

function pushAvatar(reaction?: "try-on" | "celebrate"): void {
  const save = appStore.get().save;
  if (!save?.profile.avatarId) return;
  const preview = appStore.get().ui.previewEquip;
  bridge.emit("avatar:set", {
    avatarId: save.profile.avatarId,
    equipped: preview ?? save.equipped,
    ...(reaction ? { reaction } : {}),
  });
}

function spotlightIfNew(): void {
  const save = appStore.get().save;
  bridge.emit("city:spotlight", save && !save.profile.cityHintDone && highestCompleted(save, "sorting") === 0 ? "sorting" : null);
}

/** Loads the local save (or creates one) and wires every bridge listener. */
export async function bootApp(): Promise<void> {
  const store = await openSaveStore();
  initPersistence(store);
  let save = await store.loadSave();
  if (!save) {
    save = emptySave(crypto.randomUUID(), Date.now());
    await store.writeSave(save);
  }
  const loaded = save;
  appStore.set((state) => ({
    ...state,
    save: loaded,
    ui: { ...state.ui, durableSave: store.durable, screen: loaded.profile.avatarId ? "city" : "pick" },
  }));
  applySettings(loaded.settings);
  await refreshOutbox();
  startPlatformGates(loaded.prefs.installHintDismissedAt);

  sync = new SyncService(new ApiClient(BUILD.apiBase || publicOrigin(), () => appStore.get().save?.session?.token ?? null));
  sync.start();

  bridge.on("boot:progress", (value) => setUi({ bootProgress: value }));
  bridge.on("boot:ready", () => {
    setUi({ cityReady: true });
    pushAvatar();
    spotlightIfNew();
    setMusicTrack("town");
    checkAchievements(1500);
  });
  bridge.on("city:house-tapped", () => play("tap"));
  bridge.on("city:arrived", (houseId) => openHouse(houseId));
  bridge.on("puzzle:state", (hud) => {
    const puzzle = appStore.get().ui.puzzle;
    if (puzzle?.houseId === "sorting") setUi({ hud: { ...hud, houseId: "sorting", levelId: puzzle.level.id } });
  });
  bridge.on("lab:state", (hud) => {
    const puzzle = appStore.get().ui.puzzle;
    if (puzzle?.houseId === "colors") setUi({ hud: { ...hud, houseId: "colors", levelId: puzzle.level.id } });
  });
  bridge.on("jade:state", (hud) => {
    const { puzzle, hud: before } = appStore.get().ui;
    if (puzzle?.houseId !== "tiles") return;
    setUi({ hud: { ...hud, houseId: "tiles", levelId: puzzle.level.id } });
    // The floor just got cleared on a throne level: one more walk, said once (the scene lights the throne).
    const cleared = hud.left === 0 && before?.houseId === "tiles" && before.left > 0;
    if (cleared && !hud.stuck && puzzle.level.rules.floor.some((row) => row.includes("T"))) toast(jadeVi.toThrone);
  });
  bridge.on("tower:state", (hud) => {
    const puzzle = appStore.get().ui.puzzle;
    if (puzzle?.houseId === "logic") setUi({ hud: { ...hud, houseId: "logic", levelId: puzzle.level.id } });
  });
  bridge.on("silk:state", (hud) => {
    const puzzle = appStore.get().ui.puzzle;
    if (puzzle?.houseId === "fill") setUi({ hud: { ...hud, houseId: "fill", levelId: puzzle.level.id } });
  });
  bridge.on("claw:state", (hud) => {
    const puzzle = appStore.get().ui.puzzle;
    if (puzzle?.houseId === "arcade") setUi({ hud: { ...hud, houseId: "arcade", levelId: puzzle.level.id } });
  });
  bridge.on("puzzle:won", (payload) => void completeLevel(payload));
  bridge.on("puzzle:out-of-moves", () => setUi({ outOfMoves: true }));
  bridge.on("puzzle:hint", (kind) => {
    if (kind === "undo") toast(vi.puzzle.hintUndo);
    if (kind === "none") toast(vi.puzzle.hintNone);
  });
  bridge.on("puzzle:place", ({ combo, binFull }) => {
    playPlace(combo);
    vibrate(combo >= 5 ? 18 : 12);
    if (binFull) setTimeout(() => play("bin-done"), 200);
  });
  bridge.on("puzzle:feedback", (kind) => {
    if (kind === "mistake") {
      play("error");
      vibrate([25, 40, 25]);
    } else if (kind === "win") {
      play("win");
      vibrate([20, 30, 20, 30, 40]);
    } else if (kind === "undo") play("undo");
    else if (kind === "hint") play("hint");
    else if (kind === "phase") play("phase");
    else play("pick");
  });
  bridge.on("game:context-lost", () => setUi({ contextLost: true }));

  installAudioUnlock();
  // Google Play app: consent, then the city banner and rewarded ads (no-op elsewhere).
  void startAds().then(() => retryAdClaims());
  registerTestHooks({ app: { startLevel: (houseId, levelId) => void startLevel(houseId, levelId) } });
  window.addEventListener("popstate", onBack);
  void installNativeShell();
}

// --- platform: iOS install guide + asset preload, Android shell ----------------------

const INSTALL_HINT_EVERY_MS = 24 * 3600_000;
let preloadWanted = false;

/** iOS Safari: suggest the home screen first; iOS always downloads assets before play. */
function startPlatformGates(dismissedAt: number | null): void {
  const ios = platform.ios();
  preloadWanted = ios && !preloadDoneForThisVersion();
  const suggest = ios && !platform.standalone() && (dismissedAt === null || Date.now() - dismissedAt > INSTALL_HINT_EVERY_MS);
  if (suggest) setUi({ installGuide: true });
  else if (preloadWanted) void runPreload();
}

export function continueFromInstallGuide(): void {
  unlockAudio();
  play("tap");
  setUi({ installGuide: false });
  void updateSave((save) => ({ ...save, prefs: { ...save.prefs, installHintDismissedAt: Date.now() } }));
  if (preloadWanted) void runPreload();
}

async function runPreload(): Promise<void> {
  setUi({ preload: { done: 0, total: 0, failed: false } });
  const { failed } = await preloadAll(({ done, total }) => setUi({ preload: { done, total, failed: false } }));
  if (failed > 0) setUi((ui) => ({ preload: ui.preload ? { ...ui.preload, failed: true } : null }));
  else setUi({ preload: null });
}

export function retryPreload(): void {
  void runPreload();
}

export function skipPreload(): void {
  setUi({ preload: null });
}

/** Android APK: hardware back never closes the app mid-game; confirm bundles, look for updates. */
async function installNativeShell(): Promise<void> {
  if (!platform.native()) return;
  const { App } = await import("@capacitor/app");
  await App.addListener("backButton", () => {
    const { ui } = appStore.get();
    if (ui.purchaseReveal) closePurchaseReveal();
    else if (ui.achievementPopup) dismissAchievementPopup();
    else if (ui.overlay) closeOverlay();
    else if (ui.lobbyHouse || ui.infoHouse) closeHousePopup();
    else if (ui.screen === "puzzle") {
      if (ui.paused) setPaused(false);
      else if (ui.result || ui.outOfMoves) void quitPuzzle();
      else setPaused(true);
    } else void App.minimizeApp();
  });
  await startNativeUpdates().catch(() => undefined);
}

// --- achievements ------------------------------------------------------------------

let achievementTimer: ReturnType<typeof setTimeout> | null = null;
let popupKey = 0;

/** Shows the unlock moment for anything newly reached (once per achievement). */
export function checkAchievements(delayMs = 0): void {
  if (achievementTimer) clearTimeout(achievementTimer);
  achievementTimer = setTimeout(() => {
    achievementTimer = null;
    const save = appStore.get().save;
    if (!save?.profile.avatarId) return;
    const fresh = unannounced(save);
    if (fresh.length === 0) return;
    const ids = fresh.map((def) => def.id);
    void updateSave((current) => ({ ...current, announcedAchievements: [...current.announcedAchievements, ...ids] }));
    popupKey += 1;
    setUi({ achievementPopup: { key: popupKey, ids } });
    play("achievement");
    vibrate([15, 40, 15]);
  }, delayMs);
}

export function dismissAchievementPopup(open = false): void {
  setUi({ achievementPopup: null });
  if (open) openOverlay("achievements");
}

/** Server-verified reward; returns the gold paid (0 when already claimed). */
export async function claimAchievement(id: string): Promise<number | null> {
  const def = ACHIEVEMENT_BY_ID.get(id);
  if (!def) return null;
  try {
    const response = await requireSync().claimAchievement(id);
    if (response.goldAwarded > 0) {
      play("coin");
      vibrate([10, 30, 10]);
    }
    return response.goldAwarded;
  } catch (error) {
    const code = error instanceof ApiClientError ? error.code : "NETWORK";
    const waiting = appStore.get().outbox.some((entry) => entry.status === "pending");
    toast(code === "ACHIEVEMENT_LOCKED" && waiting ? vi.achievements.waitSync : friendlyError(code));
    return null;
  }
}

// --- navigation ------------------------------------------------------------------

/** Android/browser back: close the top overlay, or pause the puzzle. */
function onBack(): void {
  const { ui } = appStore.get();
  if (ui.overlay) closeOverlay(false);
  else if (ui.lobbyHouse || ui.infoHouse) closeHousePopup(false);
  else if (ui.screen === "puzzle" && !ui.result) setPaused(true, false);
}

function pushHistory(): void {
  if (typeof history !== "undefined") history.pushState({ nano: true }, "");
}

function popHistory(fromUi: boolean): void {
  if (fromUi && typeof history !== "undefined" && history.state?.nano) history.back();
}

function blockInput(): void {
  const { ui } = appStore.get();
  bridge.emit("input:block", Boolean(ui.overlay || ui.lobbyHouse || ui.infoHouse || ui.paused || ui.tutorial || ui.result || ui.outOfMoves));
}

export function openOverlay(overlay: Overlay): void {
  play("open");
  const replacing = appStore.get().ui.overlay !== null;
  setUi({ overlay });
  if (!replacing) pushHistory();
  blockInput();
  if (overlay === "shop" || overlay === "settings" || overlay === "leaderboard" || overlay === "achievements") {
    requireSync().kick("overlay", 100);
  }
}

export function closeOverlay(fromUi = true): void {
  if (fromUi) play("close");
  setUi({ overlay: null, previewEquip: null });
  pushAvatar();
  blockInput();
  popHistory(fromUi);
}

export function openHouse(houseId: HouseId): void {
  const config = appStore.get().ui.config;
  if (isHousePlayable(config, houseId)) setUi({ lobbyHouse: houseId, infoHouse: null });
  else setUi({ infoHouse: houseId, lobbyHouse: null });
  if (houseId === "sorting" && !getSave().profile.cityHintDone) {
    void updateSave((save) => ({ ...save, profile: { ...save.profile, cityHintDone: true } }));
    bridge.emit("city:spotlight", null);
  }
  pushHistory();
  blockInput();
}

export function closeHousePopup(fromUi = true): void {
  setUi({ lobbyHouse: null, infoHouse: null });
  blockInput();
  popHistory(fromUi);
}

// --- avatar & profile ---------------------------------------------------------------

export async function pickAvatar(avatarId: AvatarId): Promise<void> {
  unlockAudio();
  play("pop");
  await updateSave((save) => withAvatar(save, avatarId));
  const wasChange = appStore.get().ui.overlay === "pick-change";
  setUi({ screen: "city", overlay: null });
  blockInput();
  pushAvatar("celebrate");
  requireSync().kick(wasChange ? "avatar-change" : "first-pick", 500);
}

export function pickRandomAvatar(): Promise<void> {
  const ids = [...ARCHETYPE_BY_ID.keys()];
  return pickAvatar(ids[Math.floor(Math.random() * ids.length)] as AvatarId);
}

export async function rename(name: string): Promise<boolean> {
  const clean = sanitizeDisplayName(name);
  if (!clean) {
    toast(vi.settings.nameRejected);
    return false;
  }
  await updateSave((save) => ({ ...save, profile: { ...save.profile, displayName: clean, profileDirty: true } }));
  requireSync().kick("rename", 300);
  toast(vi.settings.nameSaved);
  return true;
}

export async function updateSettings(patch: Partial<Settings>): Promise<void> {
  // A tick right on the toggle tap, before the save, confirms vibration is back on.
  if (patch.vibration === true) {
    setVibrationEnabled(true);
    vibrate(12);
  }
  const save = await updateSave((current) => ({ ...current, settings: { ...current.settings, ...patch } }));
  applySettings(save.settings);
  if (patch.maxDpr !== undefined) bridge.emit("settings:dpr", patch.maxDpr);
}

// --- shop & wardrobe -------------------------------------------------------------------

export function previewItem(itemId: string | null): void {
  const save = getSave();
  if (!itemId) {
    setUi({ previewEquip: null });
    pushAvatar();
    return;
  }
  const item = COSMETIC_BY_ID.get(itemId);
  if (!item) return;
  setUi({ previewEquip: { ...save.equipped, [item.slot]: item.id } });
  pushAvatar("try-on");
}

export async function equipItem(slot: EquipmentSlot, itemId: string | null): Promise<void> {
  await updateSave((save) => {
    const equipped: Equipment = { ...save.equipped };
    if (itemId) equipped[slot] = itemId;
    else delete equipped[slot];
    return { ...save, equipped, equipmentDirty: true };
  });
  setUi({ previewEquip: null });
  pushAvatar("try-on");
  play("pick");
  requireSync().kick("equip", 600);
  checkAchievements(400);
}

export async function buyItem(itemId: string): Promise<boolean> {
  const item = COSMETIC_BY_ID.get(itemId);
  if (!item) return false;
  try {
    await requireSync().purchase(itemId);
    if (getSave().wishlist === itemId) await updateSave((save) => ({ ...save, wishlist: null }));
    setUi({ previewEquip: null, purchaseReveal: itemId });
    pushAvatar("celebrate");
    play("purchase");
    vibrate([15, 30, 15, 30, 30]);
    return true;
  } catch (error) {
    toast(friendlyError(error instanceof ApiClientError ? error.code : "NETWORK"));
    return false;
  }
}

export function closePurchaseReveal(): void {
  setUi({ purchaseReveal: null });
  checkAchievements(300);
}

/** One shop item the player is saving up for; tapping again clears it. */
export async function toggleWishlist(itemId: string): Promise<void> {
  const next = getSave().wishlist === itemId ? null : itemId;
  await updateSave((save) => ({ ...save, wishlist: next }));
  play(next ? "unlock" : "tap");
  if (next) toast(vi.shop.wishlistSet);
}

// --- leaderboard & recovery ---------------------------------------------------------------

/** Remembers the rank shown, so the next visit can say "up N places". */
export function rememberRank(rank: number | null): void {
  if (rank === null || getSave().prefs.lastRank === rank) return;
  void updateSave((save) => ({ ...save, prefs: { ...save.prefs, lastRank: rank } }));
}

export function refreshLeaderboard(force = false): Promise<{ stale: boolean }> {
  return requireSync().leaderboard(force);
}

/**
 * Player-driven "update my ranking": pushes the current name and every
 * pending level result, then re-reads the board. Says honestly what is
 * still waiting (a rename inside the server's cooldown, unsent results).
 */
export async function updateMyRanking(): Promise<{ stale: boolean }> {
  if (!appStore.get().ui.online) {
    toast(vi.leaderboard.offline);
    return { stale: true };
  }
  const result = await requireSync().leaderboard(true);
  const save = appStore.get().save;
  const pending = appStore.get().outbox.some((entry) => entry.status === "pending");
  if (result.stale) toast(vi.leaderboard.updateFailed);
  else if (save?.profile.profileDirty || pending) toast(vi.leaderboard.updatePending);
  else toast(vi.leaderboard.updated);
  return result;
}

export async function issueRecoveryCode(): Promise<string | null> {
  try {
    const issued = await requireSync().issueRecovery();
    checkAchievements(1200);
    return issued.code;
  } catch (error) {
    const code = error instanceof ApiClientError ? error.code : "NETWORK";
    toast(code === "SYNC_CONFLICT" ? vi.settings.backupNeedSync : friendlyError(code));
    return null;
  }
}

export async function restoreJourney(code: string): Promise<boolean> {
  try {
    await requireSync().restore(code);
    setUi({ screen: "city", overlay: null, lobbyHouse: null, infoHouse: null });
    blockInput();
    pushAvatar("celebrate");
    bridge.emit("city:spotlight", null);
    toast(vi.settings.restored);
    return true;
  } catch (error) {
    toast(friendlyError(error instanceof ApiClientError ? error.code : "NETWORK"));
    return false;
  }
}

export function syncNow(): void {
  void requireSync().refreshStatus(true).then(() => requireSync().flush());
}

// --- puzzle flow -----------------------------------------------------------------------------

let attempt = 0;

/** A house's chapter for a level (name, music key, finale ribbon). */
export function chapterFor(houseId: PuzzleHouseId, levelId: number) {
  if (houseId === "tiles") return tileChapterOfLevel(levelId);
  if (houseId === "logic") return logicChapterOfLevel(levelId);
  if (houseId === "fill") return fillChapterOfLevel(levelId);
  if (houseId === "arcade") return arcadeChapterOfLevel(levelId);
  return houseId === "colors" ? colorChapterOfLevel(levelId) : chapterOfLevel(levelId);
}

/** Tutorial steps for an id, whichever house it belongs to. */
export function tutorialDef(houseId: PuzzleHouseId, id: string) {
  if (houseId === "tiles") return TILE_TUTORIAL_BY_ID.get(id);
  if (houseId === "logic") return TOWER_TUTORIAL_BY_ID.get(id);
  if (houseId === "fill") return SILK_TUTORIAL_BY_ID.get(id);
  if (houseId === "arcade") return CLAW_TUTORIAL_BY_ID.get(id);
  return houseId === "colors" ? COLOR_LAB_TUTORIAL_BY_ID.get(id) : TUTORIAL_BY_ID.get(id);
}

/** Loads a level through its house module; `start` puts its scene on screen. */
async function openLevel(houseId: PuzzleHouseId, levelId: number): Promise<{ puzzle: PuzzleLevel; start: () => Promise<void> }> {
  if (houseId === "tiles") {
    const module = await loadHouseModule("tiles");
    const level = await module.loadContent(levelId);
    return { puzzle: { houseId, level }, start: () => module.startLevel(level) };
  }
  if (houseId === "logic") {
    const module = await loadHouseModule("logic");
    const level = await module.loadContent(levelId);
    return { puzzle: { houseId, level }, start: () => module.startLevel(level) };
  }
  if (houseId === "colors") {
    const module = await loadHouseModule("colors");
    const level = await module.loadContent(levelId);
    return { puzzle: { houseId, level }, start: () => module.startLevel(level) };
  }
  if (houseId === "fill") {
    const module = await loadHouseModule("fill");
    const level = await module.loadContent(levelId);
    return { puzzle: { houseId, level }, start: () => module.startLevel(level) };
  }
  if (houseId === "arcade") {
    const module = await loadHouseModule("arcade");
    const level = await module.loadContent(levelId);
    return { puzzle: { houseId, level }, start: () => module.startLevel(level) };
  }
  const module = await loadHouseModule("sorting");
  const level = await module.loadContent(levelId);
  return { puzzle: { houseId, level }, start: () => module.startLevel(level) };
}

function currentModule() {
  const houseId = appStore.get().ui.puzzle?.houseId ?? "sorting";
  return loadHouseModule(houseId);
}

export async function startLevel(houseId: PuzzleHouseId, levelId: number): Promise<void> {
  if (levelId < 1 || levelId > 100) return;
  play("tap");
  setUi({ levelLoading: true, levelError: false });
  try {
    const { puzzle, start } = await openLevel(houseId, levelId);
    attempt += 1;
    const seen = getSave().profile.seenTutorials;
    const id = puzzle.level.tutorialId;
    const tutorialId = id && tutorialDef(houseId, id) && !seen.includes(id) ? id : null;
    setUi({
      screen: "puzzle",
      lobbyHouse: null,
      infoHouse: null,
      overlay: null,
      puzzle: { ...puzzle, attempt },
      hud: null,
      paidHints: 0,
      result: null,
      outOfMoves: false,
      paused: false,
      tutorial: tutorialId ? { id: tutorialId, step: 0 } : null,
    });
    await start();
    // Review levels each show one chapter's idea, so they borrow that chapter's key.
    const chapter = puzzle.houseId === "logic" ? puzzle.level.chapter : chapterFor(houseId, levelId).chapter;
    setMusicTrack("focus", CHAPTER_KEYS[chapter - 1] ?? 0);
    blockInput();
  } catch {
    setUi({ levelError: true });
    toast(vi.puzzle.loadFailed);
  } finally {
    setUi({ levelLoading: false });
  }
}

/** Advances or closes the tutorial; the focus is drawn by the scene or the HUD. */
export async function advanceTutorial(): Promise<void> {
  const { tutorial, puzzle } = appStore.get().ui;
  if (!tutorial || !puzzle) return;
  const def = tutorialDef(puzzle.houseId, tutorial.id);
  const next = tutorial.step + 1;
  if (def && next < def.steps.length) {
    setUi({ tutorial: { id: tutorial.id, step: next } });
    return;
  }
  setUi({ tutorial: null });
  blockInput();
  await updateSave((save) =>
    save.profile.seenTutorials.includes(tutorial.id)
      ? save
      : { ...save, profile: { ...save.profile, seenTutorials: [...save.profile.seenTutorials, tutorial.id] } },
  );
}

async function completeLevel(payload: {
  proof: LevelProof;
  stars: 1 | 2 | 3;
  hints: number;
  timeMs: number | null;
  bestCombo: number;
  pours?: number;
  peak?: number;
  drops?: number;
  coins?: number;
  rolls?: number;
}): Promise<void> {
  const puzzle = appStore.get().ui.puzzle;
  if (!puzzle || puzzle.houseId !== payload.proof.houseId || puzzle.level.id !== payload.proof.levelId) return;
  const { houseId, level } = puzzle;
  const save = getSave();
  const previous = bestStars(save, houseId, level.id);
  const previousTime = bestTime(save, houseId, level.id);
  const predicted = predictGold(save, appStore.get().outbox, houseId, level.id, payload.stars);
  const chapterComplete = previous === null && level.id % 10 === 0 ? chapterFor(houseId, level.id).chapter : null;
  const celebration = {
    chapterComplete,
    bestCombo: payload.bestCombo,
    houseId,
    ...(puzzle.houseId === "colors" && payload.pours !== undefined
      ? {
          pours: {
            used: payload.pours,
            optimal: puzzle.level.optimalPours,
            three: puzzle.level.threeStarPours,
            two: puzzle.level.twoStarPours,
            hinted: payload.hints > 0,
          },
        }
      : {}),
    ...(puzzle.houseId === "tiles" && payload.peak !== undefined
      ? { satchel: { peak: payload.peak, goldLine: puzzle.level.goldLine, tidiest: puzzle.level.minPeak, hinted: payload.hints > 0 } }
      : {}),
    ...(puzzle.houseId === "arcade" && payload.coins !== undefined
      ? {
          coins: {
            used: payload.coins,
            optimal: puzzle.level.optimalCoins,
            three: puzzle.level.threeStarCoins,
            two: puzzle.level.twoStarCoins,
            hinted: payload.hints > 0,
          },
        }
      : {}),
    ...(puzzle.houseId === "fill" && payload.rolls !== undefined
      ? {
          rolls: {
            used: payload.rolls,
            optimal: puzzle.level.optimalRolls,
            three: puzzle.level.threeStarRolls,
            two: puzzle.level.twoStarRolls,
            hinted: payload.hints > 0,
          },
        }
      : {}),
    ...(puzzle.houseId === "logic" && payload.drops !== undefined
      ? {
          drops: {
            used: payload.drops,
            optimal: puzzle.level.optimalDrops,
            three: puzzle.level.threeStarDrops,
            two: puzzle.level.twoStarDrops,
            hinted: payload.hints > 0,
          },
        }
      : {}),
  };
  try {
    // Persist the proof before showing success (spec §14.1).
    const entry = await commitResult(
      {
        houseId,
        levelId: level.id,
        contentVersion: level.contentVersion,
        proof: payload.proof,
        stars: payload.stars,
        predictedGold: predicted,
        createdAt: Date.now(),
        status: "pending",
      },
      (current) => recordPlayStats(recordLocalResult(current, houseId, level.id, payload.stars, payload.timeMs), Date.now(), payload.bestCombo),
    );
    setUi({
      result: {
        levelId: level.id,
        stars: payload.stars,
        previousBest: previous,
        predictedGold: predicted,
        timeMs: payload.timeMs,
        previousBestTimeMs: previousTime,
        outboxId: entry.id,
        status: appStore.get().ui.online ? "pending" : "offline",
        ...celebration,
      },
    });
    requireSync().maybeFlush();
    // Let the stars land before a badge slides in.
    checkAchievements(chapterComplete ? 3600 : 2600);
  } catch {
    setUi({
      result: {
        levelId: level.id,
        stars: payload.stars,
        previousBest: previous,
        predictedGold: 0,
        timeMs: payload.timeMs,
        previousBestTimeMs: previousTime,
        outboxId: null,
        status: "save_failed",
        ...celebration,
      },
    });
  }
  blockInput();
}

export function puzzleCommand(command: "undo" | "hint" | "restart"): void {
  play("tap");
  bridge.emit("puzzle:command", command);
  if (command === "restart") {
    // A fresh attempt: its free hints come back, bought ones were already used.
    setUi({ outOfMoves: false, result: null, paidHints: 0 });
    blockInput();
  }
}

// --- hints: a few free per attempt, then bought with server-verified gold -------------------

export const HINTS = defaultGameConfig.economy.hints;

/** Hints this attempt can still use without buying (free ones plus ones already bought). */
export function hintsLeft(): number {
  const { hud, paidHints } = appStore.get().ui;
  return Math.max(0, HINTS.freePerLevel + paidHints - (hud?.hints ?? 0));
}

/**
 * Pays the server for one more hint, then shows it. Needs the network:
 * gold only moves when the Worker confirms it.
 */
export async function buyHint(): Promise<boolean> {
  const { puzzle, paidHints } = appStore.get().ui;
  if (!puzzle) return false;
  try {
    await requireSync().buyHint(puzzle.houseId, puzzle.level.id, `${puzzle.houseId}:${puzzle.level.id}:${puzzle.attempt}:${paidHints}`);
    setUi((ui) => ({ paidHints: ui.paidHints + 1 }));
    play("coin");
    vibrate([10, 30, 10]);
    toast(vi.puzzle.hintBought);
    puzzleCommand("hint");
    return true;
  } catch (error) {
    toast(friendlyError(error instanceof ApiClientError ? error.code : "NETWORK"));
    return false;
  }
}

export function setPaused(paused: boolean, fromUi = true): void {
  setUi({ paused });
  bridge.emit("puzzle:command", paused ? "pause" : "resume");
  void currentModule().then((module) => (paused ? module.pause() : module.resume()));
  if (paused && fromUi) pushHistory();
  if (!paused) popHistory(fromUi);
  blockInput();
}

export async function quitPuzzle(): Promise<void> {
  const module = await currentModule();
  module.destroy();
  setUi({ screen: "city", puzzle: null, hud: null, paidHints: 0, result: null, outOfMoves: false, paused: false, tutorial: null });
  setMusicTrack("town");
  blockInput();
  // Back in town is a natural batch boundary: send whatever is waiting.
  if (appStore.get().outbox.some((entry) => entry.status === "pending")) requireSync().kick("city", 400);
  void retryAdClaims();
}

export function nextLevel(): void {
  const puzzle = appStore.get().ui.puzzle;
  if (puzzle && puzzle.level.id < 100) void startLevel(puzzle.houseId, puzzle.level.id + 1);
}

export function replayLevel(): void {
  const puzzle = appStore.get().ui.puzzle;
  if (puzzle) void startLevel(puzzle.houseId, puzzle.level.id);
}

// Singletons (store, services, listeners) must never be duplicated by a hot update.
if (import.meta.hot) import.meta.hot.accept(() => window.location.reload());
